import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertType, Direction, IFilterInput } from '../../graphql/schema/p4types';
import { appActions } from '../../state/modules/app';
import { useP4ToastController } from '../../views/system/NotificationsArea';

export type IWorkspaceSelectionState = {
  selectedAll: boolean;
  actionEntities: Array<any>;
  count: number;
  filters: Array<IFilterInput>;
  path: string;
};

export type IWorkspaceSelection = {
  selectedRows: Array<any>;
  selectedRowIds: Array<number>;
  selectedAll: boolean;
  enableRowSelect: boolean;
  enableDisableRowSelect: () => void;
  getSelectionState: () => IWorkspaceSelectionState;
  onRowSelect: (selectedRowIds: Array<number>, selectedRows: Array<any>, displayAlert?: boolean) => void;
  onAllRowsSelected: (all: boolean) => void;
  orderBy: string;
  orderDirection: Direction;
};

type IWorkspaceSelectionProps = {
  enableRowSelect: boolean;
  listData: { count: number }; //More to it than this.
  viewScreenName: string;
  match?: any;
  workspaceFilters: Array<IFilterInput>;
  initialSelectedRows: Array<any>;
  initialSelectedRowIds: Array<number>;
  onRowSelect?: (rows: Array<any>) => void;
  preventSelectionResetOnFilterChange?: boolean;
  currentView?: any; // Just used for sort direction - To be restructured in release/13
  initialSortColumn?: string;
  initialSortDirection?: Direction;
};

//Extracted row selection mechanisms for ease of maintenance.
export const useWorkspaceSelection: (parameters: IWorkspaceSelectionProps) => IWorkspaceSelection = ({
  enableRowSelect: defaultEnableRowSelect,
  listData,
  viewScreenName,
  match,
  workspaceFilters,
  initialSelectedRows,
  initialSelectedRowIds,
  onRowSelect: onRowSelectFromProps,
  preventSelectionResetOnFilterChange = false,
  currentView,
  initialSortColumn,
  initialSortDirection,
}) => {
  const [enableRowSelect, setEnableRowSelect] = useState(defaultEnableRowSelect);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Array<any>>(initialSelectedRows);
  const [selectedRowIds, setSelectedRowIds] = useState<Array<number>>(initialSelectedRowIds);
  const dispatch = useDispatch();
  const { dispatchSimpleToast } = useP4ToastController();
  const actionEntities = useSelector((store: any) => store.app.actionEntities);

  const onRowSelect = useCallback(
    (newSelectedRowIds: Array<number>, newSelectedRows: Array<any>, displayAlert = false) => {
      if (displayAlert && (newSelectedRowIds.length !== selectedRowIds.length || selectedAll)) {
        dispatchSimpleToast('Cleared Selection', 'Selected rows have been reset.', AlertType.Info);
      }

      setSelectedRowIds(newSelectedRowIds);
      setSelectedRows(newSelectedRows);
      onRowSelectFromProps?.(newSelectedRows); //Call props.onRowSelect if specified.

      dispatch(appActions.setActionEntities({ [viewScreenName]: newSelectedRows }));
    },
    [dispatch, dispatchSimpleToast, onRowSelectFromProps, selectedAll, selectedRowIds.length, viewScreenName]
  );

  useEffect(() => {
    if (!preventSelectionResetOnFilterChange || selectedAll) {
      onRowSelect([], []);
    }
    // Explicitly wish this to trigger on mount and when selectedAll changes, to give us a fresh selection state to work from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAll]);

  const getSelectionState = (): IWorkspaceSelectionState => {
    const thisScreenActionEntities = (actionEntities && actionEntities[viewScreenName]) || [];

    let count;
    if (listData) {
      if (enableRowSelect) {
        if (selectedAll) {
          count = listData.count - thisScreenActionEntities.length;
        } else {
          count = thisScreenActionEntities.length;
        }
      } else {
        count = listData.count;
      }
    } else {
      count = 0;
    }

    const path = (viewScreenName || match.path.substr(1)).replace(/\/+$/, '');

    return {
      actionEntities: thisScreenActionEntities,
      count: count,
      selectedAll: !enableRowSelect || selectedAll,
      filters: workspaceFilters,
      path: path,
    };
  };

  const enableDisableRowSelect = () => {
    setEnableRowSelect((prevEnableRowSelect) => {
      if (prevEnableRowSelect) {
        setSelectedAll(false);
        return false;
      }
      return true;
    });
  };

  const onAllRowsSelected = (all: boolean) => {
    setSelectedAll(all);
  };

  const orderBy = currentView?.orderBy?.[0]?.fieldName ?? initialSortColumn;
  const orderDirection = currentView?.orderBy?.[0]
    ? currentView?.orderBy?.[0]?.isDescending
      ? Direction.Desc
      : Direction.Asc
    : initialSortDirection;

  return {
    selectedRows,
    selectedRowIds,
    selectedAll,
    enableRowSelect,
    enableDisableRowSelect,
    getSelectionState,
    onRowSelect,
    onAllRowsSelected,
    orderBy: orderBy,
    orderDirection: orderDirection,
  };
};
